import React, { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useDispatch, useSelector } from 'react-redux'
import NavPane from '../NavPane/NavPane.js'
import 'video.js/dist/video-js.css'
import '@videojs/themes/dist/forest/index.css' // video.js, change this 

// Video.js styles
import videojs from 'video.js'
import 'video.js/dist/video-js.css'

const VideoPlayer = ({ video }) => {
  const [isLiked, setIsLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)

  // Video.js Refs
  const videoRef = useRef(null)
  const playerRef = useRef(null)

  const params = useParams()
  const dispatch = useDispatch()
  
  const userState = useSelector(state => state.user)

  // Extract video ID as string
  const rawVideoId = params.id || params.videoId || video?.id || video?.video_id || video?.key
  const videoId = rawVideoId ? String(rawVideoId).trim() : null

  // Safely extract active username / user_id
  const getUserId = () => {
    const u = userState?.user || userState?.userInfo || userState

    if (u?.username && u.username !== 'null') return String(u.username).trim()
    if (u?.user_id && u.user_id !== 'null') return String(u.user_id).trim()
    if (u?.id && u.id !== 'null') return String(u.id).trim()
    if (typeof u === 'string' && u !== 'null' && u !== '[object Object]') return u.trim()

    try {
      const stored = JSON.parse(localStorage.getItem('user'))
      if (stored?.username) return String(stored.username).trim()
      if (stored?.user_id) return String(stored.user_id).trim()
      if (stored?.id) return String(stored.id).trim()
    } catch (e) {}

    const directUser = localStorage.getItem('username') || localStorage.getItem('currentUser')
    return directUser ? String(directUser).trim() : null
  }

  const activeUserId = getUserId()

  // Video.js lifecycle management
  useEffect(() => {
    if (videoRef.current && !playerRef.current) {
      const videoElement = videoRef.current
      if (!videoElement) return

      playerRef.current = videojs(videoElement, {
        controls: true,
        autoplay: false,
        responsive: true,
        fluid: true
      })
    }
  }, [])

  // Handle video source updates dynamically
  useEffect(() => {
    if (playerRef.current && video?.url) {
      playerRef.current.src({ src: video.url, type: 'video/mp4' })
    }
  }, [video?.url])

  // Dispose Video.js on component unmount
  useEffect(() => {
    const player = playerRef.current
    return () => {
      if (player && !player.isDisposed()) {
        player.dispose()
        playerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (video) {
      setLikeCount(Number(video.likes) || Number(video.like_count) || 0)
    }

    if (!videoId || !activeUserId) {
      console.warn("Missing videoId or activeUserId:", { videoId, activeUserId })
      return
    }

    const recordWatchHistory = async () => {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, watched_videos')
        .ilike('user_id', activeUserId)

      const profile = profiles?.[0]
      if (!profile) return

      let watched = Array.isArray(profile.watched_videos) ? profile.watched_videos.map(String) : []

      if (!watched.includes(videoId)) {
        const updatedWatched = [videoId, ...watched]

        await supabase
          .from('profiles')
          .update({ watched_videos: updatedWatched })
          .eq('user_id', profile.user_id)
      }
    }

    const checkUserLike = async () => {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('liked_videos')
        .ilike('user_id', activeUserId)

      const profile = profiles?.[0]
      if (profile && Array.isArray(profile.liked_videos)) {
        const likes = profile.liked_videos.map(String)
        setIsLiked(likes.includes(videoId))
      }
    }

    recordWatchHistory()
    checkUserLike()
  }, [video, videoId, activeUserId])

  const sendLike = async () => {
    const isLoggedIn = userState?.isLoggedIn || userState?.user?.isLoggedIn

    if (!isLoggedIn || !activeUserId) {
      dispatch({ type: "SHOW_AUTH" })
      return
    }
    
    if (!videoId) return

    try {
      const { data: profiles, error: fetchErr } = await supabase
        .from('profiles')
        .select('user_id, liked_videos')
        .ilike('user_id', activeUserId)

      if (fetchErr || !profiles || profiles.length === 0) return

      const profile = profiles[0]
      let currentLikes = Array.isArray(profile.liked_videos) ? profile.liked_videos.map(String) : []

      const nextIsLiked = !isLiked
      const nextCount = nextIsLiked ? likeCount + 1 : Math.max(0, likeCount - 1)

      let updatedLikes = nextIsLiked
        ? [...new Set([videoId, ...currentLikes])]
        : currentLikes.filter(id => id !== videoId)

      await supabase
        .from('profiles')
        .update({ liked_videos: updatedLikes })
        .eq('user_id', profile.user_id)

      setIsLiked(nextIsLiked)
      setLikeCount(nextCount)

      const numVideoId = Number(videoId)
      if (!isNaN(numVideoId)) {
        await supabase
          .from('videos')
          .update({ likes: nextCount })
          .eq('id', numVideoId)
      }

    } catch (err) {
      console.error('Error in sendLike:', err)
    }
  }

  const showShareAlert = async () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => alert("Copied link: " + window.location.href))
      .catch(() => alert('Failed to copy link'))
  }
  
  const baseButtonStyle = {
    background: 'linear-gradient(to bottom, #383838 0%, #222222 100%, #181818 31%, #252525 100%)',
    boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.2), 0px 2px 4px rgba(0,0,0,0.5)',
    border: '1px solid #000000'
  }

  const activeButtonStyle = {
    background: 'linear-gradient(to bottom, #181818 0%, #252525 100%)',
    boxShadow: 'inset 0px 2px 4px rgba(0,0,0,0.8)',
    border: '1px solid #000000'
  }

  const glassBorderStyle = {
    background: 'linear-gradient(to bottom, rgba(66, 66, 66, 0.2), rgba(39, 39, 39, 0.1))',
    borderRadius: '8px',
    border: '1px solid rgba(236, 242, 249, 0.5)',
   
   boxShadow: `
      0 0 22px 5px rgba(0, 0, 0, 0.75),
      0 0 500px 20px white,
      inset 0 1px 1px rgba(255, 255, 255, 0.4),
      inset 0 -1px 1px rgba(0, 0, 0, 0.6)
    `,
    outline: '2px solid rgba(28, 49, 39, 0.5)',
    padding: '4px'
  }

  const formatDate = (rawDate) => {
    if (!rawDate) return ""
    try {
      const d = new Date(rawDate)
      return isNaN(d.getTime()) ? String(rawDate).split('T')[0] : d.toISOString().split('T')[0]
    } catch (e) {
      return String(rawDate).split('T')[0]
    }
  }

  return (
    <div className="-m-[30px] flex flex-row w-full h-full relative">
      
      {/* 1. NavPane Wrapper */}
      <div
        className="absolute top-0 z-10 overflow-hidden transition-all duration-200 w-[60px] lg:w-[220px]" 
        style={{ left: '-208px' }}
      >
        <NavPane activePage={"Home"} />
      </div>

      {/* 2. Video Content Wrapper */}
      <div
        className="flex-1 flex flex-col p-[30px] overflow-y-auto rounded transition-all duration-200"
        style={{ 
          paddingLeft: 'calc(max(0px, calc(50% - 900px)) + var(--nav-offset, 75px))' 
        }}
      >
        {/* Video Container with Glass Border */}
        <div 
          className="h-full w-full max-w-4xl mx-auto overflow-hidden"
          style={glassBorderStyle}
        >
          <div data-vjs-player className="w-full h-full">
<video
  ref={videoRef}
  className="video-js vjs-theme-forest vjs-big-play-centered object-contain object-center h-full w-full bg-black rounded"
/>  
          </div> 
        </div>
        
        {/* Video details */}
        <div className="flex-1 max-w-4xl mx-auto w-full pt-6">
          <div className="flex flex-col w-full md:flex-row">
            <div className="w-full">
              <div className="font-semibold flex-1 text-xl md:text-2xl pt-2 md:pt-4 mx-4 text-gray-900">
                {video ? (video.title || video.name) : ""}
              </div>
              <div className="flex">
                <div className="text-sm mx-4 pt-1 text-teal-600 font-medium">
                  {video ? (video.view_count || video.views || 0) + " Watched" : ""}
                </div>
                <div className="text-sm mx-4 pt-1 text-gray-600 font-medium">
                  {video ? formatDate(video.date) : ""}
                </div>
              </div>
            </div>

            <div className="flex w-max items-start pt-2 md:pt-4 justify-between px-2">
              <button
                style={isLiked ? activeButtonStyle : baseButtonStyle}
                className={"text-gray-200 rounded-md px-4 py-1 mx-2 transition-all hover:brightness-110 active:scale-95 " + (isLiked ? "text-teal-400 font-semibold" : "")}
                onClick={sendLike}
              >
                Like {likeCount}
              </button>
              <button
                style={baseButtonStyle}
                className="text-gray-200 rounded-md px-4 py-1 mx-2 transition-all hover:brightness-110 active:scale-95"
                onClick={showShareAlert}
              >
                Share
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VideoPlayer
